/**
 * Template Loader - Dynamic CSS loading with proper cleanup
 */

export interface Template {
  id: string;
  name: string;
  description: string;
  preview?: string;
}

export const AVAILABLE_TEMPLATES: Template[] = [
  {
    id: 'default',
    name: 'Default',
    description: 'Clean and professional design matching current styling',
  },
  {
    id: 'classic',
    name: 'Classic',
    description: 'Traditional restaurant styling with warm colors',
  },
  {
    id: 'sushi',
    name: 'Sushi',
    description: 'Dark theme with Asian aesthetic for sushi restaurants',
  },
];

let currentStyleElement: HTMLStyleElement | null = null;
let currentTemplateId: string | null = null;

/**
 * Dynamic import mapping for templates
 */
const templateImports: Record<string, () => Promise<{ default: string }>> = {
  default: () => import('../assets/templates/default.css?inline'),
  classic: () => import('../assets/templates/classic.css?inline'),
  sushi: () => import('../assets/templates/sushi.css?inline'),
};

/**
 * Load a template dynamically using Vite's ?inline import
 */
export async function loadTemplate(templateId: string): Promise<void> {
  const template = AVAILABLE_TEMPLATES.find(t => t.id === templateId);

  if (!template) {
    console.warn(`Template "${templateId}" not found. Available templates:`, AVAILABLE_TEMPLATES.map(t => t.id));
    return;
  }

  // Don't reload if already active
  if (currentTemplateId === templateId && currentStyleElement) {
    console.log(`Template "${template.name}" already active`);
    return;
  }

  // Remove previous template CSS
  if (currentStyleElement) {
    currentStyleElement.remove();
    currentStyleElement = null;
  }

  try {
    // Import CSS as string using ?inline
    const cssModule = await templateImports[templateId]();
    const cssText = cssModule.default;

    // Create and inject style element
    const styleElement = document.createElement('style');
    styleElement.setAttribute('data-template', templateId);
    styleElement.textContent = cssText;
    document.head.appendChild(styleElement);

    // Add data-template attribute to html element for higher specificity
    document.documentElement.setAttribute('data-template', templateId);

    currentStyleElement = styleElement;
    currentTemplateId = templateId;

    console.log(`Template "${template.name}" loaded successfully`);
  } catch (error) {
    console.error(`Failed to load template "${template.name}":`, error);
    throw error;
  }
}

/**
 * Get current template ID from localStorage or default
 */
export function getCurrentTemplateId(): string {
  return localStorage.getItem('selectedTemplate') || 'default';
}

/**
 * Convert backend template enum to lowercase template ID
 * Handles enum values: DEFAULT, CLASSIC, SUSHI
 */
export function normalizeTemplateEnum(templateEnum: string | null | undefined): string {
  if (!templateEnum) {
    return 'default';
  }
  return templateEnum.toLowerCase();
}

/**
 * Save template selection to localStorage
 */
export function saveTemplateSelection(templateId: string): void {
  localStorage.setItem('selectedTemplate', templateId);
}

/**
 * Initialize template system - load saved template or default
 */
export async function initializeTemplateSystem(): Promise<void> {
  const templateId = getCurrentTemplateId();
  await loadTemplate(templateId);
}

/**
 * Switch to a different template
 */
export async function switchTemplate(templateId: string): Promise<void> {
  await loadTemplate(templateId);
  saveTemplateSelection(templateId);
}
